import type { OrganizationDto, UserDataDto } from "@strtmn/sds/types"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { listOrgs, sortOrgs } from "../../api/OrgsApi"
import { getById } from "../../api/userData"
import { ErrorBanner, LoadingErrorState, RoundedCard } from "../../components"
import UserMask from "../../components/admin/UserMask"
import { isProd } from "../../config"

export function AdminUserCreateEditForm() {
  const location = useLocation()
  const [isNewUser, setIsNewUser] = useState<boolean>(true)
  const [user, setUser] = useState<UserDataDto | null>(null)
  const [allOrgs, setAllOrgs] = useState<OrganizationDto[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const params = useParams<{
    userId: string | undefined
  }>()

  useEffect(() => {
    const loadAllOrgs = async () => {
      !isProd && console.debug("ceu: > loadAllOrgs")
      setLoading(true)
      setAllOrgs(null)
      try {
        const _allOrgs = await listOrgs()
        if (!_allOrgs) {
          throw new Error("Could not load all orgs")
        }
        setAllOrgs(_allOrgs)
      } catch (err) {
        console.log(
          "ceu.loadAllOrgs: Caught an error:",
          JSON.stringify(err, null, 2)
        )
        setError(getErrorMessage(err))
      }
    }
    loadAllOrgs()
  }, [])

  useEffect(() => {
    const loadUser = async (userId: string) => {
      !isProd && console.debug("ceu: > loadUser")
      setLoading(true)
      setUser(null)
      try {
        const _user = await getById(userId)
        if (!_user) {
          throw new Error("Could not load user")
        }
        setUser(_user)
        setIsNewUser(false)
      } catch (err) {
        console.log(
          "ceu.loadUser: Caught an error:",
          JSON.stringify(err, null, 2)
        )
        setError(getErrorMessage(err))
      }
    }

    if (location.state?.isNewUser && location.state.user) {
      !isProd && console.debug("Heck, this is a new user:", location.state)
      setUser(location.state.user)
      setIsNewUser(true)
    } else if (params.userId) {
      !isProd && console.debug("userId:", params.userId)
      loadUser(params.userId)
    } else {
      console.error("No userId parameter")
    }
  }, [location.state, params.userId])

  useEffect(() => {
    if (error) {
      !isProd && console.debug("⚡ Done loading, got an error:", error)
      setLoading(false)
    } else if (allOrgs && user) {
      !isProd && console.debug("✅ Done loading, got user and allOrgs")
      setLoading(false)
    } else {
      !isProd &&
        console.debug(
          "⏳ Not done loading. user =",
          user,
          " ; allOrgs =",
          allOrgs
        )
    }
  }, [allOrgs, error, user])

  if (loading || error) {
    return (
      <LoadingErrorState
        isLoading={loading}
        loadingMessage={I18n.get("Lade Benutzerdaten, bitte warten...")}
        isError={!!error}
        errorMessage={`${I18n.get("Fehler")}: ${I18n.get(error!)}`}
      />
    )
  }

  if (!user) {
    return (
      <ErrorBanner error={I18n.get("Benutzer erstellen fehlgeschlagen!")} />
    )
  } else {
    const loadedUser = user!
    const loadedAllOrgs = sortOrgs(allOrgs!)
    return (
      <main className="p-8 pt-2">
        <div className="page-heading mb-4">
          {I18n.get(isNewUser ? "Benutzer anlegen" : "Benutzer bearbeiten")}
        </div>

        <RoundedCard>
          <UserMask
            user={loadedUser}
            isNewUser={isNewUser}
            allOrgs={loadedAllOrgs}
          />
        </RoundedCard>
      </main>
    )
  }
}
